import { motion } from "framer-motion";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useModal, useToastMessage, useViewport } from "src/contexts";
import {
  CommunityOrLotStepper,
  FooterHeight,
  ImageDisplay,
  ImageDisplayImageProps,
  StepWrapper,
  ToggleComponent,
  WarningModal,
} from "src/pages/configurator/components";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import {
  Checkbox,
  fetchAndCacheSVG,
  HBLoadingSpinner,
  HBLoadingSpinnerOnly,
  OptionPreviewImage,
  Radio,
  SelectableOptionCard,
} from "~components";
import { Css } from "~generated/css";
import { UserType } from "~generated/graphqlTypes";
import {
  cleanValidationError,
  fail,
  formatAmount,
  getDebugParts,
  getPlanBathroomRangeString,
  getPlanBedroomRangeString,
  getPlanSqftRangeString,
  queryResult,
  useTestIds,
} from "~utils";
import { createConfiguratorUrl, createLotsUrl } from "../../../RouteUrls";
import { useCurrentUser } from "../../../utils/hooks/useCurrentUser";
import { UserWarning } from "../components";
import {
  ConfiguratorContext_CheckoutConfigFragment,
  ConfiguratorContext_LoadConfigDocument,
  useConfiguratorContext_SavePlanChangeMutation,
} from "../Configurator.context.generated";
import { getFloorPlanImages } from "../Configurator.utils";
import { structureIdToLastConfigId } from "../structureConfigMap";
import { AduStep_PlanListingFragment } from "./Adu.step.generated";
import { FlowStep } from "./FlowSteps";
import { PlanStep_ProductOfferingFragment, PlanStep_StructureFragment, usePlanStepQuery } from "./Plan.step.generated";
import { usePlanSelection_CreateCheckoutConfigMutation } from "./PlanSelection.step.generated";

/**
 * This step allows the user to select a ProductOffering from a community, which may be limited for a given Structure.
 * There are also two distinct flows this component handles, each with a different route:
 * 1. Pre-config (`/communities/:communityId/lot/:structureId/plan`):
 *    The user is selecting a plan for the first time, so we create a new checkout config.
 * 2. Post-config (`/communities/:communityId/checkoutConfigs/:checkoutConfigId/plan`):
 *    The user is navigating back to the "Plan" step on an existing config and selecting a new plan.
 */
export function PlanStep() {
  const { communityId, checkoutConfig } = useConfiguratorContext();
  const { structureId: structureIdParam } = useParams();
  const { isMobile } = useViewport();

  // In the Post-config flow, if the user navigates to this route directly (or refreshes), the structureId will be undefined until
  // the context is loaded. This is the only reason we allow for temporarily skipping the query below.
  const structureId = structureIdParam ?? checkoutConfig?.structure?.id;
  const query = usePlanStepQuery({
    variables: { communityId: communityId ?? fail("Missing communityId"), structureId: structureId ?? "" },
    skip: !structureId,
    fetchPolicy: "cache-first",
  });

  if (!structureId) return <PlanStepLoadView isMobile={isMobile} />;

  return queryResult(query, {
    data: (data) => {
      const plans = data.productOfferings.entities;
      // Cache the floor plan svgs early
      plans.forEach((plan) => {
        plan.floorPlanAssets?.forEach((floorPlanAsset) => {
          void fetchAndCacheSVG(floorPlanAsset.asset.downloadUrl);
        });
      });

      const buildablePlanIds = data.structure.parent?.buildablePlans?.map((bp) => bp.id);
      return (
        <PlanStepView
          plans={plans}
          buildablePlanIds={buildablePlanIds}
          structureId={structureId}
          structure={data.structure}
        />
      );
    },
    loading: () => <PlanStepLoadView isMobile={isMobile} />,
  });
}

export function PlanStepView(props: {
  plans: PlanStep_ProductOfferingFragment[];
  buildablePlanIds: string[] | undefined;
  structureId: string | undefined;
  structure?: PlanStep_StructureFragment;
}) {
  const { plans, buildablePlanIds, structureId, structure } = props;
  const { checkoutConfig, communityId, setHasDirtyPlanSelection } = useConfiguratorContext();
  // The lot's land price is added on top of each plan's base config starting price (see getFormattedPlanDisplayPrice).
  const landPriceInCents = structure?.parent?.landPriceInCents ?? 0;
  const tid = useTestIds({}, "market-plans-step");
  const navigate = useNavigate();
  const { showModal, closeModal } = useModal();
  const { isMobile } = useViewport();
  const { currentUser } = useCurrentUser();
  const { showToast } = useToastMessage();
  const [planInfoCardHeight, setPlanInfoCardHeight] = useState(0);
  const planInfoCardRef = useRef<HTMLDivElement>(null);

  /**
   * For suburban developments, we can specify at the `Lot` level which product offerings/plans will fit on a given lot.
   * However if no buildable plans are specified, we assume all plans are buildable.
   */
  const isPlanBuildable = useCallback(
    (planId: string) => {
      if (!buildablePlanIds || buildablePlanIds.length === 0) return true;
      return buildablePlanIds.includes(planId);
    },
    [buildablePlanIds],
  );

  const initialPlanId = useMemo(
    () =>
      checkoutConfig?.productOffering?.id ??
      structure?.readyPlanConfig?.readyPlan?.id ??
      plans.find((p) => isPlanBuildable(p.id))?.id,
    [checkoutConfig, structure, plans, isPlanBuildable],
  );

  // All plan/reversed state is local — nothing auto-saves until "Next"
  // This allows the user to browse plans freely without paying the cost of initializing a full config.
  const [selectedPlanId, _setSelectedPlanId] = useState<string | undefined>(initialPlanId);
  const [isReversed, _setIsReversed] = useState(checkoutConfig?.isReversed ?? false);
  // Toggle between "Exterior | Floor Plans"
  const [viewFloorplan, setViewFloorplan] = useState(false);

  // Wrap the state setters to also signal dirty state to prevent navigation from the Header, etc.
  const savedPlanId = checkoutConfig?.productOffering?.id;
  const savedIsReversed = checkoutConfig?.isReversed ?? false;
  const setSelectedPlanId = useCallback(
    (planId: string) => {
      _setSelectedPlanId(planId);
      setHasDirtyPlanSelection(planId !== savedPlanId || isReversed !== savedIsReversed);
    },
    [savedPlanId, isReversed, savedIsReversed, setHasDirtyPlanSelection],
  );
  const setIsReversed = useCallback(
    (reversed: boolean) => {
      _setIsReversed(reversed);
      setHasDirtyPlanSelection(selectedPlanId !== savedPlanId || reversed !== savedIsReversed);
    },
    [selectedPlanId, savedPlanId, savedIsReversed, setHasDirtyPlanSelection],
  );

  const [createCheckoutConfig, { loading: creatingConfig }] = usePlanSelection_CreateCheckoutConfigMutation();
  const [savePlanChange, { loading: savingPlan }] = useConfiguratorContext_SavePlanChangeMutation();
  const isSaving = creatingConfig || savingPlan;

  const maybeDisabledReasons = useMemo(
    () =>
      checkoutConfig
        ? [checkoutConfig.canEdit, checkoutConfig.canChangeProductOffering]
        : [structure?.canChangeProductOffering],
    [checkoutConfig, structure],
  );
  const isDisabled = maybeDisabledReasons.some((r) => r && !r.allowed);

  // Sort plans based on the initial selectedPlanId on first render
  // to pull currently selected plan to the top of the list
  const sortedPlans = useMemo(() => {
    if (!selectedPlanId) return plans;
    return [...plans].sort((a, b) => +(b.id === selectedPlanId) - +(a.id === selectedPlanId));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sort once on initial render
  }, []);

  const currentPlan = plans.find((p) => p.id === selectedPlanId);
  const images = getCurrentPlanImages(viewFloorplan, currentPlan, isReversed);

  const maybeStepWarning = useMemo(() => {
    // Post-cutoff/disabled takes priority, we don't care if some plans might not be buildable in this case.
    if (isDisabled) {
      return <UserWarning title="The floor plan can no longer be changed." disabledReasons={maybeDisabledReasons} />;
    }

    // Check that all plans are buildable
    if (plans.some((p) => !isPlanBuildable(p.id))) {
      return <UserWarning title="This lot does not support all plans." />;
    }
    return null;
  }, [isDisabled, maybeDisabledReasons, plans, isPlanBuildable]);

  async function saveNewConfig() {
    if (!selectedPlanId || !structureId) return;

    const debugParts = getDebugParts();
    const result = await createCheckoutConfig({
      variables: {
        structureId,
        productOfferingId: selectedPlanId,
        isReversed,
        homeownerId: currentUser?.type === UserType.Homeowner ? currentUser.id : undefined,
        debugParts,
      },
      onError: (error) => {
        const cleanErrorMessage = cleanValidationError(error.message);
        showToast({
          variant: "error",
          title: "Save Error",
          body: `We're having trouble creating your configuration: ${cleanErrorMessage}`,
        });
      },
      // Seed the ConfiguratorContext_LoadConfig cache entry with the full config data from the
      // mutation response. This lets the provider's query (which uses cache-first) resolve instantly
      // on the next route, rather than firing a second network request to load what we already have.
      // We use cache.writeQuery (vs just relying on normalized cache writes) because Apollo needs
      // the root-level `checkoutConfig(id: ...)` reference to exist — without it, the LoadConfig
      // query would miss the cache even though the underlying CheckoutConfig entity is present.
      update: (cache, { data }) => {
        if (!data?.saveCheckoutConfig?.checkoutConfig) return;
        cache.writeQuery({
          query: ConfiguratorContext_LoadConfigDocument,
          variables: { id: data.saveCheckoutConfig.checkoutConfig.id, debugParts },
          data: { checkoutConfig: data.saveCheckoutConfig.checkoutConfig },
        });
        // We evict the checkoutConfigs list because the query is both inactive and uses dynamic variables
        // so booting it is the best way to ensure we get the latest data.
        cache.evict({ fieldName: "checkoutConfigs" });
        cache.gc();
      },
    });

    if (!result.data) return;

    const ccfgId = result.data.saveCheckoutConfig.checkoutConfig.id;
    structureIdToLastConfigId.set(structureId, ccfgId);
    navigate(createConfiguratorUrl(communityId!, ccfgId, FlowStep.Exterior));
  }

  async function saveExistingConfig() {
    if (!selectedPlanId || !checkoutConfig) return;

    // Don't bother calling `setOptionIds` b/c changing the POF will have the BE reset the ccfg.readyPlanOptions
    // and also fill in any missing/stale RPOGs, so just let it work.
    const result = await savePlanChange({
      variables: {
        input: {
          id: checkoutConfig.id,
          productOfferingId: selectedPlanId,
          isReversed,
        },
        debugParts: getDebugParts(),
      },
      onError: (error) => {
        const cleanErrorMessage = cleanValidationError(error.message);
        showToast({
          variant: "error",
          title: "Save Error",
          body: `We're having trouble saving your changes: ${cleanErrorMessage}`,
        });
      },
    });

    if (!result.data) return;
    navigate(createConfiguratorUrl(communityId!, checkoutConfig.id, FlowStep.Exterior));
  }

  async function onNext() {
    if (!selectedPlanId) return;
    setHasDirtyPlanSelection(false);

    // Pre-config: create a new checkout config
    if (!checkoutConfig) return saveNewConfig();

    const planChanged = selectedPlanId !== checkoutConfig.productOffering?.id;
    const reversedChanged = isReversed !== checkoutConfig.isReversed;

    if (planChanged) {
      // Changing plans resets all selections — confirm before saving
      showModal(
        <WarningModal
          title="Are you sure?"
          confirmationMessage="Changing plans will reset all selections."
          onContinue={() => void saveExistingConfig()}
          onClose={closeModal}
        />,
      );
    } else if (reversedChanged) {
      void saveExistingConfig();
    } else {
      navigate(createConfiguratorUrl(communityId!, checkoutConfig.id, FlowStep.Exterior));
    }
  }

  if (isSaving) {
    return (
      <div css={Css.df.fdc.aic.jcc.fg1.gap3.mt("30vh").$}>
        <HBLoadingSpinner />
        <div css={Css.t18.gray800.$}>Preparing your plan selections...</div>
        <CommunityOrLotStepper onBack={() => {}} onNext={() => {}} disabledReasons={["Loading..."]} />
      </div>
    );
  }

  return (
    <>
      {/* Mobile view is different enough that we handle it separately */}
      {isMobile ? (
        <>
          <div
            // Add padding to the bottom of this container to account for plan info card height and footer height
            css={Css.df.fdc.oa.bgWhite.pt1.px2.gap1.pbPx(planInfoCardHeight + FooterHeight + 16).$}
          >
            <PlanStepDescription />
            {maybeStepWarning}
            <div css={Css.df.fdc.gap2.$}>
              {sortedPlans.map((plan) => (
                <div key={plan.id} css={Css.df.fdc.$}>
                  <div css={Css.df.aic.jcsb.py2.$}>
                    <div css={Css.t18.$}>{plan.name}</div>
                    <div css={Css.df.gap1.aic.$}>
                      <div css={Css.t14.$}>{getFormattedPlanDisplayPrice(plan, checkoutConfig, landPriceInCents)}</div>
                      <Radio
                        selected={selectedPlanId === plan.id}
                        disabled={!isPlanBuildable(plan.id) || isDisabled}
                        onClick={() => setSelectedPlanId(plan.id)}
                      />
                    </div>
                  </div>
                  <div css={Css.ha.mhPx(200).$}>
                    <OptionPreviewImage
                      optionName={plan.name}
                      imageUrl={plan.rendering?.largePreview}
                      size={{ width: "100%", height: "100%" }}
                      hideBorder={true}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {currentPlan && (
            <motion.div
              ref={planInfoCardRef}
              css={Css.df.fdc.bgWhite.p2.ba.bcLightGray.fixed.w100.bottomPx(FooterHeight).$}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              onAnimationComplete={() => {
                if (planInfoCardRef.current) {
                  setPlanInfoCardHeight(planInfoCardRef.current.offsetHeight);
                }
              }}
            >
              <div css={Css.df.aic.jcsb.pb1.$}>
                <div css={Css.t18.$}>{currentPlan.name}</div>
                <Radio selected={true} />
              </div>
              <PlanStepCardContent
                plan={currentPlan}
                checkoutConfig={checkoutConfig}
                landPriceInCents={landPriceInCents}
              />
              <PlanReversedCheckbox
                isDisabled={isDisabled}
                autoSaving={false}
                isReversed={isReversed}
                onToggle={() => setIsReversed(!isReversed)}
              />
            </motion.div>
          )}
        </>
      ) : (
        <StepWrapper
          title="Floor Plan"
          description={<PlanStepDescription />}
          warningNode={maybeStepWarning}
          left={
            <>
              {/* Toggle Container */}
              <div css={Css.z1.absolute.topPx(24).left("50%").add("transform", "translate(-50%)").$}>
                <ToggleComponent
                  offLabel="Exterior"
                  onLabel="Floor Plans"
                  value={viewFloorplan}
                  onClick={() => setViewFloorplan(!viewFloorplan)}
                  {...tid.toggle}
                />
              </div>
              <div css={Css.df.fdc.h100.w100.if(viewFloorplan).bgOffWhite.$}>
                <ImageDisplay images={images} isReversed={isReversed} {...tid.imageDisplay} />
              </div>
            </>
          }
          right={sortedPlans.map((plan) => (
            <SelectableOptionCard
              key={plan.id}
              title={plan.name}
              description={
                <PlanStepCardContent plan={plan} checkoutConfig={checkoutConfig} landPriceInCents={landPriceInCents} />
              }
              onClick={() => setSelectedPlanId(plan.id)}
              selected={selectedPlanId === plan.id}
              disabled={!isPlanBuildable(plan.id) || isDisabled}
            />
          ))}
          rightFooter={
            <PlanReversedCheckbox
              isDisabled={isDisabled}
              autoSaving={false}
              isReversed={isReversed}
              onToggle={() => setIsReversed(!isReversed)}
            />
          }
        />
      )}
      <CommunityOrLotStepper
        onBack={() => {
          if (checkoutConfig) {
            showModal(
              <WarningModal
                title="Are you sure?"
                confirmationMessage="Selecting a new lot will require an entirely new configuration."
                onContinue={() => navigate(createLotsUrl(communityId!))}
                onClose={closeModal}
              />,
            );
          } else {
            navigate(createLotsUrl(communityId!));
          }
        }}
        onNext={onNext}
        disabledReasons={!selectedPlanId ? ["Please select a plan"] : []}
      />
    </>
  );
}

function PlanStepLoadView({ isMobile }: { isMobile: boolean }) {
  return (
    <>
      {isMobile ? (
        <div css={Css.df.fdc.oa.bgWhite.py1.px2.gap2.fg1.$}>
          <PlanStepDescription />
          {/* Center the spinner a bit more vertically*/}
          <div css={Css.df.jcc.aic.fg1.mh("55vh").$}>
            <HBLoadingSpinnerOnly />
          </div>
        </div>
      ) : (
        <StepWrapper
          loading
          loadingView="selectable"
          title="Floor Plan"
          description={<PlanStepDescription />}
          left={<ImageDisplay images={[]} />}
          right={<></>}
        />
      )}
      <CommunityOrLotStepper onBack={() => {}} onNext={() => {}} disabledReasons={["Loading..."]} />
    </>
  );
}

/**
 * If the plan is selected, use the checkout config's sales price, otherwise use the plan's base config sales price.
 * Returns formatted amount string (e.g. "$350,000") or empty string if no price available.
 *
 * `landPriceInCents` is the lot's land price, which is added on top of the plan's base config starting price
 * (the base config price is plan-level and excludes land). It is not added to the selected plan's checkout
 * config price, which already accounts for land via the backend's checkout pricing.
 */
export function getFormattedPlanDisplayPrice(
  plan: PlanStep_ProductOfferingFragment | AduStep_PlanListingFragment,
  checkoutConfig: ConfiguratorContext_CheckoutConfigFragment | undefined,
  landPriceInCents = 0,
): string {
  const isAdu = checkoutConfig?.aduProductOffering?.id === plan.id;
  const isMainHouse = checkoutConfig?.productOffering?.id === plan.id;
  const isSelectedPlan = isAdu || isMainHouse;

  if (!isSelectedPlan) {
    return formatAmount((plan.baseConfig?.salesPriceInCents ?? 0) + landPriceInCents);
  }

  // For the selected plan, use the checkout config's sales price (which reflects current selections)
  const selectedPlanPrice = isAdu ? checkoutConfig?.aduSalesPriceInCents : checkoutConfig?.salesPriceInCents;
  return formatAmount(selectedPlanPrice ?? 0);
}

/** Show the Beds / Baths / Sqft for each plan. */
function PlanStepCardContent(props: {
  plan: PlanStep_ProductOfferingFragment;
  checkoutConfig: ConfiguratorContext_CheckoutConfigFragment | undefined;
  landPriceInCents?: number;
}) {
  const { plan, checkoutConfig, landPriceInCents } = props;
  const tid = useTestIds({}, "plan-step-card-content");

  const hasBaseSalesPrice = !!plan.baseConfig?.salesPriceInCents;

  if (!hasBaseSalesPrice) {
    console.error(
      `${plan.name}'s base config '${plan.baseConfig?.name}' is being shown in checkout but has no defined sales price.`,
    );
  }

  const formattedPrice = getFormattedPlanDisplayPrice(plan, checkoutConfig, landPriceInCents);

  return (
    <div css={Css.df.fdc.gap1.$}>
      {/* NOTE: We will ALWAYS have a baseConfig as it is a validation rule to even pull POFs into the configurator in the first place. */}
      {hasBaseSalesPrice && (
        <div css={Css.t14.gray700.$} {...tid.planSalesPrice}>{`Starting at ${formattedPrice}`}</div>
      )}
      <div css={Css.df.flexWrap("wrap").colGap(3).rowGap(1).$}>
        <div css={Css.df.aic.$}>
          <p css={Css.t14.gray700.$}>{getPlanBedroomRangeString(plan)}</p>
        </div>
        <div css={Css.df.aic.$}>
          <p css={Css.t14.gray700.$}>{getPlanBathroomRangeString(plan)}</p>
        </div>
        <div css={Css.df.aic.$}>
          <p css={Css.t14.gray700.$}>{getPlanSqftRangeString(plan)}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Get the images to display for the current plan based on the view mode.
 * Returns floor plan images when viewing floor plans, otherwise returns rendering images.
 */
function getCurrentPlanImages(
  viewFloorplan: boolean,
  currentPlan: PlanStep_ProductOfferingFragment | undefined,
  isReversed: boolean,
): ImageDisplayImageProps[] {
  if (!currentPlan) return [];

  const floorPlanImages = getFloorPlanImages(currentPlan, isReversed);
  if (viewFloorplan && floorPlanImages.nonEmpty) {
    return floorPlanImages;
  }

  if (currentPlan.rendering) {
    return [
      {
        kind: "image",
        isRendering: true,
        id: currentPlan.rendering.id,
        imageUrl: currentPlan.rendering.largePreview!,
        isReversed,
        name: currentPlan.planPackage?.name,
        description: currentPlan.planPackage?.description,
      },
    ];
  }

  return [];
}

function PlanReversedCheckbox(props: {
  isDisabled: boolean;
  autoSaving: boolean;
  isReversed: boolean;
  onToggle: () => void;
}) {
  const { isDisabled, autoSaving, isReversed, onToggle } = props;
  const tid = useTestIds({}, "plan-reversed-checkbox");
  return (
    <div css={Css.df.aic.gap1.ifMobile.mt3.$}>
      <Checkbox disabled={isDisabled || autoSaving} selected={isReversed} onClick={onToggle} {...tid.isReversed} />
      <p css={Css.ml1.$}>Reverse Plan</p>
    </div>
  );
}

function PlanStepDescription() {
  return (
    <div css={Css.df.fdc.gap1.t14.lh("1.25em").gray.$}>
      Choose from one of our thoughtfully designed floor plans to get started. Next, you can pick your exterior style
      and add on more features.
    </div>
  );
}
